import type { FieldPath, FieldValues } from "react-hook-form"
import type { BaseQueryError } from "./model"

const HANDLED_ERRORS = [400, 401, 403, 422, 429]

export type Violation<Values extends FieldValues> = {
  readonly propertyPath: FieldPath<Values> | ""
  readonly title: string
}

type ApiError<Values extends FieldValues> = {
  readonly violations?: Violation<Values>[]
}

type MessageError = {
  readonly message: string
}

function isMessageError(data: unknown): data is MessageError {
  return (
    !!data &&
    typeof data === "object" &&
    "message" in data &&
    typeof data.message === "string"
  )
}

export const parseUnknownError = <Values extends FieldValues = FieldValues>(
  e: unknown,
) => {
  if (!e || typeof e !== "object") {
    return false
  }

  if (!("status" in e) || !("data" in e)) {
    return false
  }

  if (!e.data || typeof e.data !== "object" || !("violations" in e.data)) {
    return false
  }

  return parseError<Values>(e as BaseQueryError)
}

export const parseError = <Values extends FieldValues>(
  error: BaseQueryError,
): Violation<Values>[] | false => {
  if (
    typeof error.status !== "number" ||
    !HANDLED_ERRORS.includes(error.status)
  ) {
    return false
  }

  const violations = (error.data as ApiError<Values>).violations
  if (!violations) {
    // Errors with a plain message field (login, rate limiting, etc.)
    if (!isMessageError(error.data) || !error.data.message) {
      return false
    }

    return [
      {
        propertyPath: "",
        title: error.data.message,
      },
    ]
  }

  return violations
    .slice() // Clone array to avoid TypeError: 0 is read-only
    .reverse() // Show last validation error of each field
}
